import { useEffect, useState, useCallback } from "react";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import { CitationList, ContentButton } from "../elements/card-group";
import { useDesignOntologyGraphPagination } from "@/hooks/use-design-ontology-graph-pagination";
import { Pagination } from "../elements/pagination";
import { RotateCw, Route, GitBranch, FileText, Puzzle, Trash2, Plus, Pencil, Sparkles, Eye } from "lucide-react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import CustomTooltip from "../elements/custom-tooltip";
import { useQueryClient } from "@tanstack/react-query";
import { useBulkDeleteDesignOntology } from "@/service-hooks/use-bulk-delete-design-ontology-service";
import ConfirmDialog from "../elements/confirm-dialog";
import { toast } from "sonner";
import { useCreateDesignNode } from "@/service-hooks/use-create-design-node-service";
import { useUpdateDesignNode } from "@/service-hooks/use-update-design-node-service";
import { DesignNodeDialog } from "../elements/design-node-dialog";
import { useGetNodeCitations } from "@/service-hooks/use-get-node-citations-service";
import { NodeCitation } from "@/service-hooks/use-get-functional-graph-service";
import { ComponentItem } from "@/service-hooks/use-get-design-ontology-graph-service";
import { getDocumentsSignedUrlById } from "@/service-hooks/use-get-document-service";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { useProjectRole } from "@/context/project-role-context";
import { useUser } from "@/hooks/use-user";
import { useGenerateDesignEmbeddings } from "@/service-hooks/use-generate-design-embeddings-service";

type DesignLabel = "UserJourney" | "Flow" | "Page" | "Component";

export default function DesignTabLayout() {
  const {
    userJourneys,
    flows,
    pages,
    components,

    userJourneysTotal,
    flowsTotal,
    pagesTotal,
    componentsTotal,

    userJourneyPage,
    flowPage,
    pagePage,
    componentPage,
    userJourneysTotalPages,
    flowsTotalPages,
    pagesTotalPages,
    componentsTotalPages,

    selectedUserJourney,
    selectedFlow,
    selectedPage,

    handleUserJourneyClick,
    handleFlowClick,
    handlePageClick,

    handleUserJourneyNextPage,
    handleUserJourneyPrevPage,
    handleFlowNextPage,
    handleFlowPrevPage,
    handlePageNextPage,
    handlePagePrevPage,
    handleComponentNextPage,
    handleComponentPrevPage,

    userJourneyQuery,
    flowQuery,
    pageQuery,
    componentQuery,

    isLoadingFlows,
    isLoadingPages,
    isLoadingComponents,

    flowSearch,
    setFlowSearch,
    pageSearch,
    setPageSearch,
    userJourneySearch,
    setUserJourneySearch,
    componentSearch,
    setComponentSearch,

    projectUuid,
    setSelectedUserJourney,
    setSelectedFlow,
    setSelectedPage,
  } = useDesignOntologyGraphPagination();

  const queryClient = useQueryClient();
  const { getAuthHeaders } = useAuthHeaders();
  const { isViewer } = useProjectRole();
  const { user } = useUser();
  const isAdmin = user?.role === "ADMIN";
  const [isEmbeddingConfirmOpen, setEmbeddingConfirmOpen] = useState(false);

  const { mutate: generateDesignEmbeddings } = useGenerateDesignEmbeddings(
    (data) => {
      toast.success(data.message || "Embedding generation started successfully");
    },
    (error) => {
      toast.error(error.message || "Failed to start embedding generation. Please try again.");
    }
  );

  const handleRegenerateEmbeddings = () => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    if (!projectUuid) {
      toast.error("Project UUID not found");
      return;
    }
    setEmbeddingConfirmOpen(true);
  };

  const handleConfirmRegenerateEmbeddings = () => {
    if (projectUuid) {
      generateDesignEmbeddings({ projectUuid });
    }
    setEmbeddingConfirmOpen(false);
  };

  // ── Citation state ──
  const [selectedComponent, setSelectedComponent] = useState<ComponentItem | null>(null);

  // Collapse all expanded levels when a snapshot is restored, so the user
  // doesn't see stale flows/pages/components. Fresh data will be fetched on
  // the next click.
  useEffect(() => {
    const handleSnapshotRestored = () => {
      setSelectedUserJourney(null);
      setSelectedFlow(null);
      setSelectedPage(null);
      setSelectedComponent(null);
    };
    window.addEventListener("snapshot-restored", handleSnapshotRestored);
    return () => {
      window.removeEventListener("snapshot-restored", handleSnapshotRestored);
    };
  }, [setSelectedUserJourney, setSelectedFlow, setSelectedPage]);

  const { data: userJourneyCitations } = useGetNodeCitations(
    selectedUserJourney?.citationRefs,
    projectUuid
  );
  const { data: flowCitations } = useGetNodeCitations(
    selectedFlow?.citationRefs,
    projectUuid
  );
  const { data: pageCitations } = useGetNodeCitations(
    selectedPage?.citationRefs,
    projectUuid
  );
  const { data: componentCitations } = useGetNodeCitations(
    selectedComponent?.citationRefs,
    projectUuid
  );

  const handleNodeCitationClick = async (citation: NodeCitation) => {
    const citationType = citation.type;

    if (citationType === "code" || citationType === "prompt" || citationType === "user") return;

    if (citationType === "jira" || citationType === "figma" || citationType === "confluence") {
      window.open(citation.reference, "_blank", "noopener,noreferrer");
      return;
    }

    const docId = citation.reference;
    if (!docId) return;
    try {
      const signedUrl = await queryClient.fetchQuery({
        queryKey: ["getSignedDocUrl", docId],
        queryFn: () => {
          const headers = getAuthHeaders();
          return getDocumentsSignedUrlById(docId, headers);
        },
        staleTime: 300000,
      });
      window.open(signedUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const renderCitations = (
    label: string,
    badgeClass: string,
    title: string,
    citations: NodeCitation[] | undefined
  ) => {
    if (!citations || citations.length === 0) return null;
    return (
      <div className="bg-surface-card rounded-lg p-3 border border-surface-border mt-3">
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-xs font-medium px-2 py-0.5 rounded uppercase tracking-wider ${badgeClass}`}>
            {label}
          </span>
          <span className="text-sm text-white truncate">{title}</span>
          <span className="text-xs text-muted">— Citations</span>
        </div>
        <CitationList
          citations={citations}
          onCitationClick={handleNodeCitationClick}
        />
      </div>
    );
  };

  // ── Bulk-delete state ──
  const [deleteMode, setDeleteMode] = useState<Record<DesignLabel, boolean>>({
    UserJourney: false,
    Flow: false,
    Page: false,
    Component: false,
  });
  const [selectedIds, setSelectedIds] = useState<Record<DesignLabel, Set<string>>>({
    UserJourney: new Set(),
    Flow: new Set(),
    Page: new Set(),
    Component: new Set(),
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteLabel, setPendingDeleteLabel] = useState<DesignLabel | null>(null);

  // ── Create / Edit / View dialog state ──
  const [isNodeDialogOpen, setNodeDialogOpen] = useState(false);
  const [dialogLabel, setDialogLabel] = useState<DesignLabel | "">("");
  const [dialogNode, setDialogNode] = useState<any>(null);
  const [isDialogReadOnly, setIsDialogReadOnly] = useState(false);

  const refetchAfterMutation = (label?: string) => {
    queryClient.invalidateQueries({ queryKey: ["design-ontology-graph"] });
    queryClient.invalidateQueries({ queryKey: ["node-counts", projectUuid] });
    if (!label || label === "UserJourney") userJourneyQuery.refetch();
    if (!label || label === "Flow") flowQuery.refetch();
    if (!label || label === "Page") pageQuery.refetch();
    if (!label || label === "Component") componentQuery.refetch();
  };

  const { mutate: createDesignNode } = useCreateDesignNode((res, err) => {
    if (err) {
      toast.error(err.message || "Failed to create node");
    } else {
      toast.success(`${res?.label || "Node"} created successfully`);
      refetchAfterMutation(res?.label);
    }
  });

  const { mutate: updateDesignNode } = useUpdateDesignNode((res, err) => {
    if (err) {
      toast.error(err.message || "Failed to update node");
    } else {
      toast.success(`${res?.label || "Node"} updated successfully`);
      refetchAfterMutation(res?.label);
    }
  });

  const handleOpenCreateDialog = (label: DesignLabel) => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    setDialogLabel(label);
    setDialogNode(null);
    setIsDialogReadOnly(false);
    setNodeDialogOpen(true);
  };

  const handleOpenEditDialog = (label: DesignLabel, node: any) => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    setDialogLabel(label);
    setDialogNode(node);
    setIsDialogReadOnly(false);
    setNodeDialogOpen(true);
  };

  // View-only entry point used by the Eye icon on each row for the Viewer
  // role — opens the same DesignNodeDialog but skips the edit-block toast.
  // The dialog itself becomes read-only via `isReadOnly`.
  const handleOpenViewDialog = (label: DesignLabel, node: any) => {
    setDialogLabel(label);
    setDialogNode(node);
    setIsDialogReadOnly(true);
    setNodeDialogOpen(true);
  };

  const handleCloseNodeDialog = () => {
    setNodeDialogOpen(false);
    setDialogNode(null);
    setDialogLabel("");
    setIsDialogReadOnly(false);
  };

  const handleSubmitNodeDialog = (data: Record<string, any>) => {
    if (!dialogLabel) return;

    if (dialogNode) {
      updateDesignNode({
        uuid: projectUuid,
        id: dialogNode.id,
        body: { label: dialogLabel, data },
      });
    } else {
      // Inject parent context for create
      const payloadData: Record<string, any> = { ...data };
      if (dialogLabel === "Flow" && selectedUserJourney) {
        payloadData.userJourneyIds = [selectedUserJourney.id];
      } else if (dialogLabel === "Page" && selectedFlow) {
        payloadData.flowIds = [selectedFlow.id];
      } else if (dialogLabel === "Component" && selectedPage) {
        payloadData.pageIds = [selectedPage.id];
      }

      createDesignNode({
        uuid: projectUuid,
        body: { label: dialogLabel, data: payloadData },
      });
    }
    handleCloseNodeDialog();
  };

  const toggleDeleteMode = (label: DesignLabel) => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    setDeleteMode((prev) => ({ ...prev, [label]: !prev[label] }));
    setSelectedIds((prev) => ({ ...prev, [label]: new Set() }));
  };

  const toggleSelect = (label: DesignLabel, id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev[label]);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...prev, [label]: next };
    });
  };

  const { mutate: bulkDelete, isPending: isDeleting } =
    useBulkDeleteDesignOntology((_, error) => {
      if (error) {
        toast.error(error.message || "Bulk delete failed");
      } else {
        toast.success("Deleted successfully");
        queryClient.invalidateQueries({ queryKey: ["design-ontology-graph"] });
        queryClient.invalidateQueries({ queryKey: ["node-counts", projectUuid] });
        userJourneyQuery.refetch();
        flowQuery.refetch();
        pageQuery.refetch();
        componentQuery.refetch();

        // Clear selections for deleted level and reset child state
        if (pendingDeleteLabel) {
          const label = pendingDeleteLabel;
          setDeleteMode((prev) => ({ ...prev, [label]: false }));
          setSelectedIds((prev) => ({ ...prev, [label]: new Set() }));

          if (label === "UserJourney") {
            setSelectedUserJourney(null);
            setSelectedFlow(null);
            setSelectedPage(null);
            setSelectedComponent(null);
          } else if (label === "Flow") {
            setSelectedFlow(null);
            setSelectedPage(null);
            setSelectedComponent(null);
          } else if (label === "Page") {
            setSelectedPage(null);
            setSelectedComponent(null);
          } else if (label === "Component") {
            setSelectedComponent(null);
          }
        }
      }
      setPendingDeleteLabel(null);
    });

  const handleBulkDeleteClick = (label: DesignLabel) => {
    if (selectedIds[label].size === 0) return;
    setPendingDeleteLabel(label);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = useCallback(() => {
    if (!pendingDeleteLabel) return;
    bulkDelete({
      projectUuid: projectUuid,
      label: pendingDeleteLabel,
      ids: Array.from(selectedIds[pendingDeleteLabel]),
    });
    setConfirmOpen(false);
  }, [pendingDeleteLabel, selectedIds, projectUuid, bulkDelete]);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["design-ontology-graph"] });
    queryClient.invalidateQueries({ queryKey: ["node-counts", projectUuid] });
    userJourneyQuery.refetch();
    if (selectedUserJourney) flowQuery.refetch();
    if (selectedFlow) pageQuery.refetch();
    if (selectedPage) componentQuery.refetch();
  };

  // ── Helpers ──
  const labelDisplayNames: Record<DesignLabel, string> = {
    UserJourney: "user journeys",
    Flow: "flows",
    Page: "pages",
    Component: "components",
  };

  // Whether the parent context required for creating a child node is selected
  const canCreate = (label: DesignLabel): boolean => {
    if (label === "UserJourney") return true;
    if (label === "Flow") return !!selectedUserJourney;
    if (label === "Page") return !!selectedFlow;
    if (label === "Component") return !!selectedPage;
    return false;
  };

  const renderSectionHeader = (
    label: DesignLabel,
    title: string,
    currentLength: number,
    total: number
  ) => (
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-3">
        <h6 className="text-lg text-white font-semibold">{title}</h6>
        <span className="text-sm text-muted bg-surface-dark px-1.5 py-0.5 rounded">
          {currentLength} of {total}
        </span>
      </div>
      <div className="flex gap-2">
        {deleteMode[label] ? (
          <>
            <Button
              variant="destructive"
              size="sm"
              disabled={selectedIds[label].size === 0 || isDeleting}
              onClick={() => handleBulkDeleteClick(label)}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete ({selectedIds[label].size})
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleDeleteMode(label)}
              className="border-surface-border text-white"
            >
              Cancel
            </Button>
          </>
        ) : (
          !isViewer && (
            <>
              <CustomTooltip text={`Add new ${labelDisplayNames[label].slice(0, -1)}`} side="top">
                <Button
                  variant="outline"
                  size="icon"
                  disabled={!canCreate(label)}
                  onClick={() => handleOpenCreateDialog(label)}
                  className="border-surface-border text-white hover:bg-surface-hover hover:border-primary hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </CustomTooltip>
              <CustomTooltip text={`Bulk delete ${labelDisplayNames[label]}`} side="top">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => toggleDeleteMode(label)}
                  className="border-surface-border text-white hover:bg-surface-hover hover:border-red-500 hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </CustomTooltip>
            </>
          )
        )}
      </div>
    </div>
  );

  // Edit (pencil) button shown on hover for editors. Viewers get the
  // renderViewerCard variant below, which wraps the eye inside the same card
  // as the text to mirror the Functional-tab Persona pattern.
  const renderEditButton = (label: DesignLabel, item: any) => {
    if (isViewer) return null;
    return (
      <CustomTooltip text="Edit" side="top">
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenEditDialog(label, item);
          }}
          className="opacity-0 group-hover:opacity-100 h-7 w-7 text-muted hover:text-white hover:bg-surface-hover transition-opacity"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </CustomTooltip>
    );
  };

  // Viewer-only card row: full-width bordered card containing the item text
  // and an outline eye button at the right — mirrors the Persona card in the
  // Functional tab (w-full card, outline eye button on the right edge).
  const renderViewerCard = (
    label: DesignLabel,
    item: any,
    displayText: string,
    isActive: boolean,
    onClick: () => void,
    rightSlot?: React.ReactNode,
  ) => (
    <div
      onClick={onClick}
      className={`min-w-0 w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors ${
        isActive
          ? "bg-primary text-button-text"
          : "bg-surface text-text-primary hover:bg-surface-hover"
      }`}
    >
      <span className="text-base truncate">{displayText}</span>
      <div className="flex items-center gap-2 flex-shrink-0">
        {rightSlot}
        <CustomTooltip text={`View ${label}`} side="top">
          <Button
            variant="outline"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenViewDialog(label, item);
            }}
            className="border-surface-border text-white hover:bg-surface-hover hover:border-primary"
          >
            <Eye className="h-4 w-4" />
          </Button>
        </CustomTooltip>
      </div>
    </div>
  );

  return (
    <div className={`space-y-2 overflow-y-auto ${CUSTOM_SCROLLBAR}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-xl font-semibold text-white mb-1">
            Design Ontology
          </h2>
          <p className="text-sm text-muted">
            User journeys, flows, pages, and components
          </p>
        </div>
        <div className="flex gap-2">
          <CustomTooltip text="Refresh" side="bottom">
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              className="border-surface-border text-white hover:bg-surface-hover hover:border-primary"
            >
              <RotateCw className="h-4 w-4" />
            </Button>
          </CustomTooltip>

          {isAdmin && !isViewer && (
            <CustomTooltip text="Re-generate Embeddings" side="bottom">
              <Button
                variant="outline"
                size="icon"
                onClick={handleRegenerateEmbeddings}
                className="border-surface-border text-white hover:bg-surface-hover hover:border-primary"
              >
                <Sparkles className="h-4 w-4" />
              </Button>
            </CustomTooltip>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {/* ──────────── User Journeys ──────────── */}
        <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
          {renderSectionHeader(
            "UserJourney",
            "User Journeys",
            userJourneys?.length || 0,
            userJourneysTotal
          )}

          <div className="space-y-2">
            <input
              type="text"
              placeholder="Search user journeys..."
              value={userJourneySearch}
              onChange={(e) => setUserJourneySearch(e.target.value)}
              className="w-full text-text-primary px-2 py-1.5 bg-surface-input border border-surface-border rounded placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {userJourneyQuery.isLoading ? (
              <div className="text-center py-4">
                <div className="flex flex-col items-center space-y-3">
                  <div className="w-10 h-10 bg-surface-dark rounded-full flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                  </div>
                  <div>
                    <p className="text-white font-medium">Loading user journeys...</p>
                    <p className="text-muted text-sm">Please wait while we fetch your user journeys</p>
                  </div>
                </div>
              </div>
            ) : userJourneys && userJourneys.length > 0 ? (
              <>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
                  {userJourneys.map((item, index) => (
                    <div
                      key={item.id || index}
                      className={`relative group flex items-center gap-2 rounded-lg transition-all duration-200 ${
                        deleteMode["UserJourney"] && selectedIds["UserJourney"].has(item.id)
                          ? "bg-gradient-to-r from-primary/20 to-primary/5 pl-2 py-1"
                          : deleteMode["UserJourney"]
                          ? "hover:bg-surface-hover/50 pl-2 py-1"
                          : ""
                      }`}
                    >
                      {deleteMode["UserJourney"] && (
                        <Checkbox
                          checked={selectedIds["UserJourney"].has(item.id)}
                          onCheckedChange={() => toggleSelect("UserJourney", item.id)}
                        />
                      )}
                      {isViewer ? (
                        renderViewerCard(
                          "UserJourney",
                          item,
                          item.name,
                          selectedUserJourney?.id === item.id,
                          () => handleUserJourneyClick(item),
                        )
                      ) : (
                        <>
                          <div className="min-w-0">
                            <ContentButton
                              content={item.name}
                              onClick={() =>
                                deleteMode["UserJourney"]
                                  ? toggleSelect("UserJourney", item.id)
                                  : handleUserJourneyClick(item)
                              }
                              isActive={
                                deleteMode["UserJourney"]
                                  ? selectedIds["UserJourney"].has(item.id)
                                  : selectedUserJourney?.id === item.id
                              }
                              contentSize="text-base"
                            />
                          </div>
                          {!deleteMode["UserJourney"] && renderEditButton("UserJourney", item)}
                        </>
                      )}
                    </div>
                  ))}
                </div>

                <Pagination
                  currentItems={userJourneys.length}
                  totalItems={userJourneysTotal}
                  currentPage={userJourneyPage}
                  totalPages={userJourneysTotalPages}
                  isLoading={userJourneyQuery.isLoading}
                  onPrevious={handleUserJourneyPrevPage}
                  onNext={handleUserJourneyNextPage}
                  canGoPrevious={userJourneyPage > 1}
                  canGoNext={userJourneyPage < userJourneysTotalPages}
                  itemName="user journeys"
                  showPageNumbers={true}
                  mode="pages"
                />
              </>
            ) : (
              <div className="text-center py-4">
                <Route className="w-10 h-10 text-muted mx-auto mb-3" />
                <p className="text-muted">No user journeys found</p>
              </div>
            )}

            {selectedUserJourney &&
              renderCitations(
                "User Journey",
                "bg-purple-500/20 text-purple-400",
                selectedUserJourney.name,
                userJourneyCitations
              )}
          </div>
        </div>

        {/* ──────────── Flows ──────────── */}
        {selectedUserJourney && (
          <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
            {renderSectionHeader("Flow", "Flows", flows?.length || 0, flowsTotal)}

            <div>
              <input
                type="text"
                placeholder="Search flows..."
                value={flowSearch}
                onChange={(e) => setFlowSearch(e.target.value)}
                className="w-full text-text-primary px-2 py-1.5 bg-surface-input border border-surface-border rounded mb-2 placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {isLoadingFlows ? (
                <div className="text-center py-3 text-muted">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-muted border-t-transparent rounded-full animate-spin"></div>
                    Loading flows...
                  </div>
                </div>
              ) : flows && flows.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
                    {flows.map((flow) => (
                      <div
                        key={flow.id}
                        className={`relative group flex items-center gap-2 rounded-lg transition-all duration-200 ${
                          deleteMode["Flow"] && selectedIds["Flow"].has(flow.id)
                            ? "bg-gradient-to-r from-primary/20 to-primary/5 pl-2 py-1"
                            : deleteMode["Flow"]
                            ? "hover:bg-surface-hover/50 pl-2 py-1"
                            : ""
                        }`}
                      >
                        {deleteMode["Flow"] && (
                          <Checkbox
                            checked={selectedIds["Flow"].has(flow.id)}
                            onCheckedChange={() => toggleSelect("Flow", flow.id)}
                          />
                        )}
                        {isViewer ? (
                          renderViewerCard(
                            "Flow",
                            flow,
                            flow.name,
                            selectedFlow?.id === flow.id,
                            () => handleFlowClick(flow),
                          )
                        ) : (
                          <>
                            <div className="min-w-0">
                              <ContentButton
                                content={flow.name}
                                onClick={() =>
                                  deleteMode["Flow"]
                                    ? toggleSelect("Flow", flow.id)
                                    : handleFlowClick(flow)
                                }
                                isActive={
                                  deleteMode["Flow"]
                                    ? selectedIds["Flow"].has(flow.id)
                                    : selectedFlow?.id === flow.id
                                }
                                contentSize="text-base"
                              />
                            </div>
                            {!deleteMode["Flow"] && renderEditButton("Flow", flow)}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  <Pagination
                    currentItems={flows.length}
                    totalItems={flowsTotal}
                    currentPage={flowPage}
                    totalPages={flowsTotalPages}
                    isLoading={flowQuery.isLoading}
                    onPrevious={handleFlowPrevPage}
                    onNext={handleFlowNextPage}
                    canGoPrevious={flowPage > 1}
                    canGoNext={flowPage < flowsTotalPages}
                    itemName="flows"
                    showPageNumbers={true}
                    mode="pages"
                  />
                </>
              ) : (
                <div className="text-center py-3">
                  <GitBranch className="w-5 h-5 text-muted mx-auto mb-1.5" />
                  <p className="text-muted text-sm">No flows found for this user journey</p>
                </div>
              )}

              {selectedFlow &&
                renderCitations(
                  "Flow",
                  "bg-blue-500/20 text-blue-400",
                  selectedFlow.name,
                  flowCitations
                )}
            </div>
          </div>
        )}

        {/* ──────────── Pages ──────────── */}
        {selectedFlow && (
          <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
            {renderSectionHeader("Page", "Pages", pages?.length || 0, pagesTotal)}

            <div>
              <input
                type="text"
                placeholder="Search pages..."
                value={pageSearch}
                onChange={(e) => setPageSearch(e.target.value)}
                className="w-full text-text-primary px-2 py-1.5 bg-surface-input border border-surface-border rounded mb-2 placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {isLoadingPages ? (
                <div className="text-center py-3 text-muted">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-muted border-t-transparent rounded-full animate-spin"></div>
                    Loading pages...
                  </div>
                </div>
              ) : pages && pages.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
                    {pages.map((page) => (
                      <div
                        key={page.id}
                        className={`relative group flex items-center gap-2 rounded-lg transition-all duration-200 ${
                          deleteMode["Page"] && selectedIds["Page"].has(page.id)
                            ? "bg-gradient-to-r from-primary/20 to-primary/5 pl-2 py-1"
                            : deleteMode["Page"]
                            ? "hover:bg-surface-hover/50 pl-2 py-1"
                            : ""
                        }`}
                      >
                        {deleteMode["Page"] && (
                          <Checkbox
                            checked={selectedIds["Page"].has(page.id)}
                            onCheckedChange={() => toggleSelect("Page", page.id)}
                          />
                        )}
                        {isViewer ? (
                          renderViewerCard(
                            "Page",
                            page,
                            page.name,
                            selectedPage?.id === page.id,
                            () => handlePageClick(page),
                          )
                        ) : (
                          <>
                            <div className="min-w-0">
                              <ContentButton
                                content={page.name}
                                onClick={() =>
                                  deleteMode["Page"]
                                    ? toggleSelect("Page", page.id)
                                    : handlePageClick(page)
                                }
                                isActive={
                                  deleteMode["Page"]
                                    ? selectedIds["Page"].has(page.id)
                                    : selectedPage?.id === page.id
                                }
                                contentSize="text-base"
                              />
                            </div>
                            {!deleteMode["Page"] && renderEditButton("Page", page)}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  <Pagination
                    currentItems={pages.length}
                    totalItems={pagesTotal}
                    currentPage={pagePage}
                    totalPages={pagesTotalPages}
                    isLoading={pageQuery.isLoading}
                    onPrevious={handlePagePrevPage}
                    onNext={handlePageNextPage}
                    canGoPrevious={pagePage > 1}
                    canGoNext={pagePage < pagesTotalPages}
                    itemName="pages"
                    showPageNumbers={true}
                    mode="pages"
                  />
                </>
              ) : (
                <div className="text-center py-3">
                  <FileText className="w-5 h-5 text-muted mx-auto mb-1.5" />
                  <p className="text-muted text-sm">No pages found for this flow</p>
                </div>
              )}

              {selectedPage &&
                renderCitations(
                  "Page",
                  "bg-cyan-500/20 text-cyan-400",
                  selectedPage.name,
                  pageCitations
                )}
            </div>
          </div>
        )}

        {/* ──────────── Components ──────────── */}
        {selectedPage && (
          <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
            {renderSectionHeader(
              "Component",
              "Components",
              components?.length || 0,
              componentsTotal
            )}

            <div>
              <input
                type="text"
                placeholder="Search components..."
                value={componentSearch}
                onChange={(e) => setComponentSearch(e.target.value)}
                className="w-full text-text-primary px-2 py-1.5 bg-surface-input border border-surface-border rounded mb-2 placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {isLoadingComponents ? (
                <div className="text-center py-3 text-muted">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-muted border-t-transparent rounded-full animate-spin"></div>
                    Loading components...
                  </div>
                </div>
              ) : components && components.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
                    {components.map((comp) => (
                      <div
                        key={comp.id}
                        className={`relative group flex items-center gap-2 rounded-lg transition-all duration-200 ${
                          deleteMode["Component"] && selectedIds["Component"].has(comp.id)
                            ? "bg-gradient-to-r from-primary/20 to-primary/5 pl-2 py-1"
                            : deleteMode["Component"]
                            ? "hover:bg-surface-hover/50 pl-2 py-1"
                            : ""
                        }`}
                      >
                        {deleteMode["Component"] && (
                          <Checkbox
                            checked={selectedIds["Component"].has(comp.id)}
                            onCheckedChange={() => toggleSelect("Component", comp.id)}
                          />
                        )}
                        {isViewer ? (
                          renderViewerCard(
                            "Component",
                            comp,
                            comp.name,
                            selectedComponent?.id === comp.id,
                            () =>
                              setSelectedComponent(
                                selectedComponent?.id === comp.id ? null : comp,
                              ),
                            comp.type ? (
                              <span
                                className={`text-xs font-semibold px-1.5 py-0.5 rounded border whitespace-nowrap uppercase tracking-wide ${
                                  selectedComponent?.id === comp.id
                                    ? "bg-transparent border-primary/30 text-primary"
                                    : "bg-primary/15 text-primary border-primary/30"
                                }`}
                                style={
                                  selectedComponent?.id === comp.id
                                    ? { color: "white", borderColor: "white" }
                                    : undefined
                                }
                              >
                                {comp.type}
                              </span>
                            ) : null,
                          )
                        ) : (
                          <>
                            <button
                              onClick={() =>
                                deleteMode["Component"]
                                  ? toggleSelect("Component", comp.id)
                                  : setSelectedComponent(
                                      selectedComponent?.id === comp.id ? null : comp
                                    )
                              }
                              className={`flex min-w-0 items-center gap-2 px-2 py-1.5 text-base rounded-md text-left ${
                                deleteMode["Component"] && selectedIds["Component"].has(comp.id)
                                  ? "bg-primary text-button-text"
                                  : !deleteMode["Component"] && selectedComponent?.id === comp.id
                                  ? "bg-primary text-button-text"
                                  : "bg-surface text-text-primary"
                              }`}
                            >
                              <span className="truncate">{comp.name}</span>
                              {comp.type && (
                                <span
                                  className={`ml-2 text-xs font-semibold px-1.5 py-0.5 rounded border whitespace-nowrap uppercase tracking-wide ${
                                    deleteMode["Component"] && selectedIds["Component"].has(comp.id)
                                      ? "bg-transparent border-primary/30 text-primary"
                                      : !deleteMode["Component"] && selectedComponent?.id === comp.id
                                      ? "bg-transparent border-primary/30 text-primary"
                                      : "bg-primary/15 text-primary border-primary/30"
                                  }`}
                                  style={
                                    (deleteMode["Component"] && selectedIds["Component"].has(comp.id)) ||
                                    (!deleteMode["Component"] && selectedComponent?.id === comp.id)
                                      ? { color: 'white', borderColor: 'white' }
                                      : undefined
                                  }
                                >
                                  {comp.type}
                                </span>
                              )}
                            </button>
                            {!deleteMode["Component"] && renderEditButton("Component", comp)}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  <Pagination
                    currentItems={components.length}
                    totalItems={componentsTotal}
                    currentPage={componentPage}
                    totalPages={componentsTotalPages}
                    isLoading={componentQuery.isLoading}
                    onPrevious={handleComponentPrevPage}
                    onNext={handleComponentNextPage}
                    canGoPrevious={componentPage > 1}
                    canGoNext={componentPage < componentsTotalPages}
                    itemName="components"
                    showPageNumbers={true}
                    mode="pages"
                  />
                </>
              ) : (
                <div className="text-center py-3">
                  <Puzzle className="w-5 h-5 text-muted mx-auto mb-1.5" />
                  <p className="text-muted text-sm">No components found for this page</p>
                </div>
              )}

              {selectedComponent &&
                renderCitations(
                  "Component",
                  "bg-green-500/20 text-green-400",
                  selectedComponent.name,
                  componentCitations
                )}
            </div>
          </div>
        )}
      </div>

      {/* Create / Edit / View dialog */}
      <DesignNodeDialog
        isOpen={isNodeDialogOpen}
        onClose={handleCloseNodeDialog}
        onSubmit={handleSubmitNodeDialog}
        node={dialogNode}
        label={dialogLabel}
        projectUuid={projectUuid}
        isReadOnly={isDialogReadOnly}
      />

      {/* Bulk-delete confirmation dialog */}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={handleConfirmDelete}
        title="Confirm Bulk Delete"
        description={
          pendingDeleteLabel
            ? `Are you sure you want to delete ${selectedIds[pendingDeleteLabel].size} ${labelDisplayNames[pendingDeleteLabel]}? This will also delete all nested children and cannot be undone.`
            : ""
        }
        confirmText="Delete"
        cancelText="Cancel"
      />

      <ConfirmDialog
        open={isEmbeddingConfirmOpen}
        onOpenChange={() => setEmbeddingConfirmOpen(false)}
        onConfirm={handleConfirmRegenerateEmbeddings}
        title="Confirm Embedding Regeneration"
        description="Re-generating embeddings is an expensive operation that will recalculate all semantic embeddings for this project. This process may take several minutes. Are you sure you want to proceed?"
      />
    </div>
  );
}
